import React, { useState, useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import { alert as byzzerAlert, openModal } from '@/components/form';
import { useUser } from "@/contexts/UserContext";
import { getAccessLevel } from "@/utils/storiesUtil";
import { storySelectorAttributes } from "@/utils/storySelectorUtil";
import { useBetterNavigate } from "@/utils";
import { useTenantApi } from "@/hooks/useTenantApi";
import { useStoryRunService } from "@/services/storiesRun.service";
import { alertMessages } from "@/config/globalVars";
import { NavigationAnalyticsType } from "@/types/AnalyticsTypes";

const wrapperClass = classnames('stories-library');

export const useCanCreateStory = () => {

    const { subscription, user, subscribedSkus, company } = useUser();
    const { getMySubscriptionUsage } = useTenantApi();
    const userAccessLevel = parseInt(getAccessLevel(company?.accessLevel) ?? '0');
    const { getStoryDefaultRunConfigBySku } = useStoryRunService();
    const [remainingCoreRuns, setRemainingCoreRuns] = useState<number>(0);
    const navigate = useBetterNavigate();

    const validateStoryCountAndShow = async (storyConfig: ProductWithMeta, analytics?: NavigationAnalyticsType) => {
        if (user?.role === 'viewer') {
            openModal({
                closeOnClickOutside: true,
                title: "Sorry! You don't have access to do that.",
                content: (
                    <>
                        <p>As a viewer, you don't have access to run a new story. </p>
                        <p>Reach out to your account admin to upgrade your member type to user.</p>
                    </>
                ),
                className: `${wrapperClass}__viewer-access-modal`,
                headerType: 'normal',
            });
            return;
        }

        let subscribed = subscribedSkus.includes(storyConfig.sku);

        if (subscribed) {

            let [usage] = await Promise.all([getMySubscriptionUsage()]);
            const defaultValues = getStoryDefaultRunConfigBySku(storyConfig.sku);

            if (userAccessLevel === 5) {
                storyConfig.metadata.env.reportSelectors = storyConfig.metadata?.env?.reportSelectors.filter(
                    (e) => e !== 'market:option5'
                );
            }

            storyConfig = {
                ...storyConfig,
                // @ts-expect-error: metadata is not defined in the type.  todo: update type
                reportDetails: {
                    reportType: storyConfig?.type,
                    reportSelectors: storyConfig.metadata?.env?.reportSelectors
                        ? storySelectorAttributes(storyConfig?.metadata?.env?.reportSelectors)
                        : {},
                    productSku: storyConfig?.sku,
                    storyType: storyConfig.metadata?.env?.storyType,
                    reportRunValue: storyConfig.metadata?.env?.reportRunValue,
                }
            };

            if (storyConfig?.type === 'story' && usage) {
                if (usage?.basicReports?.limit === 0) {
                    // setLoading(false);
                    return byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.noTeamRunsLeft}</p>
                            </>
                        ),
                    });
                } else if (usage?.basicReports?.limit - usage?.basicReports?.used <= 0) {
                    // setLoading(false);
                    return byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.usedAllPurchasedCredits}</p>
                            </>
                        ),
                    });
                } else if (
                    usage?.basicReports?.limit - usage?.basicReports?.used - storyConfig.metadata?.env?.reportRunValue < 0
                ) {
                    // setLoading(false);
                    return byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.upgradeSubscription}</p>
                            </>
                        ),
                    });
                }
                if (
                    usage?.basicReports?.limit - usage?.basicReports?.used > 0 &&
                    usage?.basicReports?.limit -
                    usage?.basicReports?.used -
                    storyConfig?.metadata?.env?.reportRunValue >=
                    0
                ) {
                    localStorage.setItem('storySku', storyConfig?.sku);
                    localStorage.setItem('dataSpecificationValues', '{}');
                    localStorage.removeItem('selectorsConfigurations');
                    localStorage.removeItem('selectedObjectives');

                    navigate('/dashboard/story_builder', {
                        params: {},
                        state: {
                            storyConfig: {
                                ...storyConfig,
                            }, //will need change later 10-19-20 to pass all data, not conditionally pass only some.
                            defaultValues: defaultValues,
                            remainingReportRuns: remainingCoreRuns,
                            analytics: analytics ? analytics : {}
                        },
                    });
                }
            }
            // setLoading(false);
        }
    };

    return validateStoryCountAndShow;
};

export default useCanCreateStory;